import {
    voucherNursingCareAssessmentPlaceEnumOptions,
} from './../../../../../../../../../types/src/common/enum/voucher-nursing-care-assessment-place.enum';
import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { NursingCareAssessmentUpsertDialogStore } from './nursing-care-assessment-upsert-dialog.store';
import { Validators } from '@angular/forms';
import {
    VoucherNursingCareAssessmentCreateInput,
    VoucherNursingCareAssessmentGridItemObject,
    VoucherNursingCareAssessmentUpdateInput,
    VoucherObject,
    WorkGridItemObject,
} from '@it2go/types';
import { TranslateService } from '@ngx-translate/core';
import { fb } from '../../../../../shared/shared.global-service';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { contentGlobalSuccess } from '../../../../content.error';
import { LoggedUserGlobalStore } from '../../../../../../store/logged-user.global-store';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { formAddText } from '../../../../../../utils/form.utils';
import {
    allMeasuredValues,
} from '../../../../../../components/medical-service/work/work-detail/work-detail-measured-values/work-detail-measured-values.types';
import { localDate } from '../../../../../shared/pipes/local-date.pipe';
import { SessionService } from '../../../../../../services/session.service';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import {
    MultiSelectInputComponent,
} from '../../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { ChipModule } from '@openng/optimus-ui/chip';
import { UserChipComponent } from '../../../../../../components/kit/user-chip/user-chip.component';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import {
    TextAreaInputComponent,
} from '../../../../../../components/kit/input/text-area-input/text-area-input.component';
import { FormatPersonPipe } from '../../../../../shared/pipes/format-person.pipe';

@Component({
    selector: 'app-nursing-care-assessment-upsert-dialog',
    templateUrl: './nursing-care-assessment-upsert-dialog.component.html',
    styleUrl: './nursing-care-assessment-upsert-dialog.component.scss',
    providers: [NursingCareAssessmentUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        ButtonComponent,
        SelectInputComponent,
        MultiSelectInputComponent,
        ChipModule,
        UserChipComponent,
        InputWysiwygComponent,
        TextAreaInputComponent,
        FormatPersonPipe,
    ],
})
export class NursingCareAssessmentUpsertDialogComponent extends BaseDialogComponent {

    public readonly store = inject(NursingCareAssessmentUpsertDialogStore);
    public readonly loggedUserGlobalStore = inject(LoggedUserGlobalStore);
    public readonly translate = inject(TranslateService);
    public readonly session = inject(SessionService);

    public readonly wysiwyg = viewChild<InputWysiwygComponent>(InputWysiwygComponent);

    protected prefix = 'folder.voucher.nursingCarePlan.createAssessment.';

    protected voucher!: VoucherObject;
    protected folderId!: number;
    protected assessment!: VoucherNursingCareAssessmentGridItemObject | null;

    protected placeOptions = voucherNursingCareAssessmentPlaceEnumOptions;

    protected form = fb.group({
        date: [new Date(), Validators.required],
        place: ['', Validators.required],
        assessment: [''],
        peoplePresent: [''],
        assessedBy: [[this.loggedUserGlobalStore.currentUserId()], Validators.required],
    });

    protected userScroll = computed(() => {
        return new InfiniteScrollStore(
            this.store.api.getUsers,
            {
                filter: [
                    [{
                        column: 'serviceId',
                        value: [this.store.serviceId()?.toString() || '0'],
                        operator: 'EQ',
                    }],
                ],
            },
            true,
        );
    });

    protected addInputSummaryLoading = false;
    protected addMeassuringLoading = false;

    constructor() {
        super();

        this.voucher = this.dialogConfig.data.voucher;
        this.folderId = this.dialogConfig.data.folderId;
        this.assessment = this.dialogConfig.data.assessment;

        if (this.assessment) {
            this.form.patchValue({
                date: new Date(this.assessment.date),
                place: this.assessment.place,
                assessment: this.assessment.assessment,
                peoplePresent: this.assessment.peoplePresent,
                assessedBy: this.assessment.assessedBy,
            });
        }
    }

    public override async save() {
        if (isInvalid(this.form)) return;

        let data = {
            ...this.form.value,
            id: this.assessment?.id ?? undefined,
        } as unknown as VoucherNursingCareAssessmentCreateInput & VoucherNursingCareAssessmentUpdateInput;

        if (this.assessment) {
            await this.store.api.updateNursingCareAssessment(data);

            this.wysiwyg()?.resetBackupValues();

            contentGlobalSuccess.next('common.updated');
        } else {
            data = { ...data, voucherId: this.voucher.id };
            await this.store.api.createNursingCareAssessment(data);

            this.wysiwyg()?.resetBackupValues();

            contentGlobalSuccess.next('common.created');
        }
        this.dialogRef.close();
    }

    protected async addMeasuring() {
        this.addMeassuringLoading = true;

        const filter = new FilterBuilder();
        filter.where('voucherId', this.voucher.id)
            .where('dateFrom', [
                this.voucher.issueDate,
                this.voucher.validTo,
            ], 'BETWEEN')
            .limit(100);
        const visits = await this.store.api.getWorks(filter.filter());

        const measurements: string[] = (visits?.['items'] || []).map((visit: WorkGridItemObject) => {
            return this.getMeasurements(visit);
        });

        const text = measurements.join('<br>');
        formAddText(this.form, 'assessment', text);

        this.addMeassuringLoading = false;
    }

    protected async addInputSummary() {
        this.addInputSummaryLoading = true;

        const carePlan = await this.store.api.getNursingCarePlan({ voucherId: this.voucher.id });
        formAddText(this.form, 'assessment', carePlan?.summary || '');

        this.addInputSummaryLoading = false;
    }

    private testAndWrite(obj: any, name: string, prefix = '') {
        return obj[name] ? this.translate.instant(prefix + name) + ': ' + obj[name] : null;
    }

    private getMeasurements(visit: WorkGridItemObject) {
        const measurements = allMeasuredValues.map((mv) => {
            return this.testAndWrite(visit.workData, mv.id, 'enum.measuredValues.');
        });

        if (!measurements.some(x => x))
            return null;

        return `${localDate(visit.dateFrom, this.session, 'd.M.yyyy H:mm')}: ${measurements.filter(f => !!f).join(', ')}`;
    }
}
